import React from "react";
import { colors } from "../../../theme/colors";
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  ListItemIcon,
  Chip,
  MenuItem,
  ThemeProvider,
  Paper,
  Container,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { CustomTextField } from "../../../components/common-components/CustomTextField";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import WorkIcon from "@mui/icons-material/Work";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DescriptionIcon from "@mui/icons-material/Description";
import CreateIcon from "@mui/icons-material/Create";
import BusinessIcon from "@mui/icons-material/Business";
import SchoolIcon from "@mui/icons-material/School";
import CodeIcon from "@mui/icons-material/Code";
import PeopleIcon from "@mui/icons-material/People";
import DoneIcon from "@mui/icons-material/Done";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";

import {
  CustomSelect,
  CustomSelectField,
} from "../../../components/common-components/CustomSelect";
import { CustomStyledCircularProgress } from "../../../components/common-components/CustomStyledCircularProgress";
import GlobalTalentLocationPicker from "../../../components/location-picker/GlobalTalentLocationPicker";
import {
  CreateJDContainer,
  FormSection,
  StyledCard,
  StyledSearchButton,
  SearchButtonContainer,
  HeaderContainer,
  WhyJoinContainer,
  PQContainer,
  theme,
} from "./style";

const PROMPT_SUGGESTIONS = [
  "Senior React Frontend Engineer with Redux & TypeScript",
  "Python Backend Developer with Django & REST API",
  "Full Stack Node.js & React Developer for SaaS product",
  "DevOps Engineer with AWS, Docker & Kubernetes",
];

const getSkillsLists = (requirements) => {
  const req = requirements || {};
  const hasNewShape =
    (req.mandatory_skills && req.mandatory_skills.length > 0) ||
    (req.optional_skills && req.optional_skills.length > 0);
  return {
    mandatory: hasNewShape
      ? req.mandatory_skills || []
      : req.technical_skills || [],
    optional: hasNewShape ? req.optional_skills || [] : [],
  };
};

const CreateNewJobDescription = ({
  jdDetails,
  newJDDescriptions,
  handleSetNewJDDescription,
  handleCreateNewJD,
  jdLevel,
  setjdLevel,
  level,
  locationInput,
  locationDropdownOpen,
  setLocationDropdownOpen,
  selectedCountry,
  countryOptions,
  citySearchQuery,
  setCitySearchQuery,
  loadingCities,
  filteredCities,
  handleCountrySelect,
  handleCitySelect,
  handleLocationDropdownClose,
  handleLocationClear,
  handleLocationRemove,
  organisationName,
  handleSetOrganisationName,
  isSubmitting,
  scrollResultRef,
  copyStatus,
  handleCopyJD,
  handleExport,
  handleExportDoc,
}) => {
  const handleSuggestionClick = (suggestion) => {
    handleSetNewJDDescription({ target: { value: suggestion } });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="tab__container">
        <Container sx={{ padding: "0px !important" }} maxWidth="lg">
          {/* Header Section */}
          <HeaderContainer>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: colors.primaryLight,
                color: colors.primary,
                px: 2,
                py: 0.6,
                borderRadius: "20px",
                mb: 1.5,
                fontWeight: 600,
                fontSize: "0.85rem",
              }}
            >
              <AutoAwesomeIcon fontSize="small" />
              AI-POWERED GENERATOR
            </Box>
            <Typography className="primary-heading" variant="h4" gutterBottom>
              Create Job Description
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: "auto" }}
            >
              Generate comprehensive, structured, and professional job descriptions in seconds using AI assistance.
            </Typography>
          </HeaderContainer>

          {/* Form Section */}
          <CreateJDContainer>
            <Paper className="create-jd-paper" elevation={0}>
              <Typography variant="h6" className="job-req-heading">
                <WorkIcon color="primary" />
                Job Requirements
              </Typography>

              <Typography variant="body2" className="page-subtitle">
                Define the key qualifications, location, and requirements for generating tailored job descriptions
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormSection>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <SchoolIcon fontSize="small" sx={{ color: colors.primary }} />
                      <Typography variant="h6" className="form-label" sx={{ mb: 0 }}>
                        Experience Level
                      </Typography>
                    </Box>
                    <CustomSelect
                      options={level}
                      value={jdLevel}
                      getOptionLabel={(option) => option.label}
                      onChange={(event, value) => {
                        setjdLevel(value);
                      }}
                      disabled={isSubmitting}
                      renderInput={(params) => (
                        <CustomSelectField
                          {...params}
                          label="Select Level"
                          variant="outlined"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              color: "#000",
                              borderRadius: "12px",
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <MenuItem
                          {...props}
                          sx={{
                            color: "#000",
                            "&:hover": {
                              backgroundColor: `${colors.primary} !important`,
                              color: "#fff",
                            },
                          }}
                        >
                          {option.label || ""}
                        </MenuItem>
                      )}
                    />
                  </FormSection>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormSection>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <LocationOnIcon fontSize="small" sx={{ color: colors.primary }} />
                      <Typography variant="h6" className="form-label" sx={{ mb: 0 }}>
                        Location
                      </Typography>
                    </Box>
                    <GlobalTalentLocationPicker
                      multiple
                      disabled={isSubmitting}
                      locationInput={locationInput}
                      locationDropdownOpen={locationDropdownOpen}
                      setLocationDropdownOpen={setLocationDropdownOpen}
                      selectedCountry={selectedCountry}
                      countryOptions={countryOptions}
                      citySearchQuery={citySearchQuery}
                      setCitySearchQuery={setCitySearchQuery}
                      loadingCities={loadingCities}
                      filteredCities={filteredCities}
                      handleCountrySelect={handleCountrySelect}
                      handleCitySelect={handleCitySelect}
                      handleLocationDropdownClose={handleLocationDropdownClose}
                      handleLocationClear={handleLocationClear}
                      handleLocationRemove={handleLocationRemove}
                    />
                  </FormSection>
                </Grid>
              </Grid>

              <FormSection sx={{ mt: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <BusinessIcon fontSize="small" sx={{ color: colors.primary }} />
                  <Typography variant="h6" className="form-label" sx={{ mb: 0 }}>
                    Organisation Name
                  </Typography>
                </Box>
                <CustomTextField
                  placeholder="e.g. Acme Corporation"
                  fullWidth
                  value={organisationName}
                  onChange={handleSetOrganisationName}
                  disabled={isSubmitting}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      color: "#000",
                    },
                  }}
                />
              </FormSection>

              <FormSection sx={{ mt: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <DescriptionIcon fontSize="small" sx={{ color: colors.primary }} />
                  <Typography variant="h6" className="form-label" sx={{ mb: 0 }}>
                    Job Description Details
                  </Typography>
                </Box>
                <CustomTextField
                  placeholder="Describe the role, responsibilities, required skills, or key expectations..."
                  fullWidth
                  multiline
                  rows={3}
                  value={newJDDescriptions}
                  onChange={handleSetNewJDDescription}
                  disabled={isSubmitting}
                  sx={{
                    color: "#000",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      color: "#000",
                    },
                  }}
                />

                <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontWeight: 600,
                    }}
                  >
                    <LightbulbOutlinedIcon fontSize="small" sx={{ fontSize: 16 }} />
                    Quick Suggestions:
                  </Typography>
                  {PROMPT_SUGGESTIONS.map((suggestion, idx) => (
                    <Chip
                      key={idx}
                      label={suggestion}
                      size="small"
                      clickable
                      onClick={() => handleSuggestionClick(suggestion)}
                      disabled={isSubmitting}
                      sx={{
                        fontSize: "0.75rem",
                        backgroundColor: "#f1f5f9",
                        color: "#334155",
                        fontWeight: 500,
                        "&:hover": {
                          backgroundColor: colors.primaryLight,
                          color: colors.primary,
                        },
                      }}
                    />
                  ))}
                </Box>
              </FormSection>

              <SearchButtonContainer>
                <StyledSearchButton
                  variant="contained"
                  size="large"
                  onClick={handleCreateNewJD}
                  disabled={isSubmitting || !newJDDescriptions.trim()}
                  startIcon={
                    isSubmitting ? null : <AutoAwesomeIcon />
                  }
                  endIcon={
                    isSubmitting ? (
                      <CustomStyledCircularProgress size={20} sx={{ color: "#ffffff" }} />
                    ) : (
                      <CreateIcon />
                    )
                  }
                  sx={{
                    minWidth: 240,
                    py: 1.4,
                    fontSize: "1.05rem",
                    fontWeight: 600,
                    borderRadius: "14px",
                  }}
                >
                  {isSubmitting ? "Generating JD..." : "Generate Job Description"}
                </StyledSearchButton>
              </SearchButtonContainer>
            </Paper>
          </CreateJDContainer>

          <Box sx={{ marginTop: "2rem" }} ref={scrollResultRef}>
            {jdDetails && (
              <StyledCard sx={{ mt: 4, position: "relative", overflow: "hidden" }}>
                <Box
                  sx={{
                    height: "5px",
                    background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryHover || colors.primary} 100%)`,
                    width: "100%",
                  }}
                />

                <CardHeader
                  title={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                        width: "100%",
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          sx={{
                            color: "#0f172a",
                            lineHeight: 1.3,
                            fontSize: { xs: "1.25rem", sm: "1.5rem" },
                          }}
                        >
                          {jdDetails.job_title}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flexShrink: 0,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1.25,
                          alignItems: "center",
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PictureAsPdfIcon sx={{ color: "#dc2626" }} />}
                          onClick={handleExport}
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 600,
                            borderColor: "#cbd5e1",
                            color: "#334155",
                            "&:hover": {
                              borderColor: "#dc2626",
                              backgroundColor: "#fef2f2",
                            },
                          }}
                        >
                          Export PDF
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ArticleOutlinedIcon sx={{ color: "#2563eb" }} />}
                          onClick={handleExportDoc}
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 600,
                            borderColor: "#cbd5e1",
                            color: "#334155",
                            "&:hover": {
                              borderColor: "#2563eb",
                              backgroundColor: "#eff6ff",
                            },
                          }}
                        >
                          Export DOC
                        </Button>
                      </Box>
                    </Box>
                  }
                  subheader={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1.5,
                        rowGap: 1,
                        mt: 1.5,
                      }}
                    >
                      {jdDetails.company && (
                        <Chip
                          icon={<BusinessIcon style={{ fontSize: 16 }} />}
                          label={jdDetails.company}
                          size="small"
                          sx={{
                            backgroundColor: colors.primaryLight,
                            color: colors.primary,
                            fontWeight: 600,
                            borderRadius: "6px",
                          }}
                        />
                      )}
                      {jdDetails.job_type && (
                        <Chip
                          label={jdDetails.job_type}
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ borderRadius: "6px", fontWeight: 600 }}
                        />
                      )}
                      {jdDetails.experience_level && (
                        <Chip
                          label={jdDetails.experience_level}
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ borderRadius: "6px", fontWeight: 600 }}
                        />
                      )}
                      {jdDetails.years_of_experience && (
                        <Chip
                          label={jdDetails.years_of_experience}
                          color="primary"
                          variant="outlined"
                          size="small"
                          sx={{ borderRadius: "6px", fontWeight: 600 }}
                        />
                      )}
                      {jdDetails.location && (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontWeight: 500,
                          }}
                        >
                          <LocationOnIcon sx={{ fontSize: 18, color: colors.primary }} />
                          {jdDetails.location}
                        </Typography>
                      )}
                      {copyStatus ? (
                        <Button
                          size="small"
                          sx={{
                            color: "#16a34a",
                            fontWeight: 600,
                            textTransform: "none",
                            backgroundColor: "#f0fdf4",
                            px: 1.5,
                            borderRadius: "6px",
                          }}
                          startIcon={<DoneIcon fontSize="small" />}
                        >
                          Copied
                        </Button>
                      ) : (
                        <Tooltip title="Copy JD text" placement="top" arrow>
                          <IconButton
                            size="small"
                            sx={{
                              backgroundColor: "#f1f5f9",
                              "&:hover": { backgroundColor: colors.primaryLight },
                            }}
                            onClick={handleCopyJD}
                          >
                            <ContentCopyIcon sx={{ color: colors.primary, fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  }
                  sx={{
                    pb: 2,
                    alignItems: "flex-start",
                    "& .MuiCardHeader-content": {
                      overflow: "hidden",
                      width: "100%",
                    },
                  }}
                />

                <CardContent sx={{ pt: 1, px: { xs: 2, sm: 4 }, pb: 4 }}>
                  <Box sx={{ mb: 4, p: 2.5, backgroundColor: "#f8fafc", borderRadius: "12px", borderLeft: `4px solid ${colors.primary}` }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      <DescriptionIcon color="primary" />
                      Job Overview
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ lineHeight: 1.7, color: "#334155" }}
                    >
                      {jdDetails.job_description}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Responsibilities */}
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      <WorkIcon color="primary" />
                      Key Responsibilities
                    </Typography>
                    <List dense sx={{ pl: 0 }}>
                      {jdDetails.responsibilities?.length > 0 &&
                        jdDetails.responsibilities.map(
                          (responsibility, index) => (
                            <ListItem
                              key={index}
                              sx={{
                                py: 0.75,
                                px: 1.5,
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "flex-start",
                                "&:hover": { backgroundColor: "#f8fafc" },
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: 24,
                                  alignSelf: "flex-start",
                                  pt: 0.5,
                                }}
                              >
                                <FiberManualRecordIcon
                                  sx={{ fontSize: 8, color: colors.primary }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={responsibility}
                                sx={{
                                  m: 0,
                                  "& .MuiTypography-root": {
                                    color: "#334155",
                                    lineHeight: 1.6,
                                  },
                                }}
                              />
                            </ListItem>
                          )
                        )}
                    </List>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontWeight: 700,
                        color: "#0f172a",
                      }}
                    >
                      <CheckCircleOutlineIcon color="primary" />
                      Requirements & Skillset
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: "14px",
                            height: "100%",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              mb: 1.5,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              fontWeight: 700,
                              color: "#0f172a",
                            }}
                          >
                            <SchoolIcon color="primary" fontSize="small" />
                            Education
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                            {jdDetails.requirements.education || "Bachelor's degree in relevant field or equivalent experience"}
                          </Typography>

                          <Typography
                            variant="subtitle1"
                            sx={{
                              mb: 1.5,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              fontWeight: 700,
                              color: "#0f172a",
                            }}
                          >
                            <WorkIcon color="primary" fontSize="small" />
                            Experience Required
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            {jdDetails.requirements.experience}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: "14px",
                            height: "100%",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          {(() => {
                            const { mandatory, optional } = getSkillsLists(
                              jdDetails.requirements
                            );
                            return (
                              <>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    mb: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    fontWeight: 700,
                                    color: "#0f172a",
                                  }}
                                >
                                  <CodeIcon color="primary" fontSize="small" />
                                  Mandatory Skills
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                                  {mandatory.length > 0 ? (
                                    mandatory.map((skill, index) => (
                                      <Tooltip title={skill} arrow placement="top" key={`m-${index}`}>
                                        <Chip
                                          label={skill}
                                          size="small"
                                          sx={{
                                            backgroundColor: colors.primary,
                                            color: "#ffffff",
                                            fontWeight: 600,
                                            borderRadius: "8px",
                                            maxWidth: "100%",
                                            "& .MuiChip-label": {
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              display: "block",
                                            },
                                          }}
                                        />
                                      </Tooltip>
                                    ))
                                  ) : (
                                    <Typography variant="body2" color="text.disabled">—</Typography>
                                  )}
                                </Box>

                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    mb: 1.5,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    fontWeight: 700,
                                    color: "#0f172a",
                                  }}
                                >
                                  <StarOutlineIcon color="primary" fontSize="small" />
                                  Optional Skills
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                  {optional.length > 0 ? (
                                    optional.map((skill, index) => (
                                      <Tooltip title={skill} arrow placement="top" key={`o-${index}`}>
                                        <Chip
                                          label={skill}
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            borderColor: colors.primary,
                                            color: colors.primary,
                                            fontWeight: 600,
                                            borderRadius: "8px",
                                            maxWidth: "100%",
                                            "& .MuiChip-label": {
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              display: "block",
                                            },
                                          }}
                                        />
                                      </Tooltip>
                                    ))
                                  ) : (
                                    <Typography variant="body2" color="text.disabled">—</Typography>
                                  )}
                                </Box>
                              </>
                            );
                          })()}
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: "14px",
                            height: "100%",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              mb: 1.5,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              fontWeight: 700,
                              color: "#0f172a",
                            }}
                          >
                            <PeopleIcon color="primary" fontSize="small" />
                            Soft Skills
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {jdDetails.requirements.soft_skills?.length > 0 ? (
                              jdDetails.requirements.soft_skills.map(
                                (skill, index) => (
                                  <Tooltip title={skill} arrow placement="top" key={`s-${index}`}>
                                    <Chip
                                      label={skill}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#e2e8f0",
                                        color: "#334155",
                                        fontWeight: 500,
                                        borderRadius: "8px",
                                        maxWidth: "100%",
                                        "& .MuiChip-label": {
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          display: "block",
                                        },
                                      }}
                                    />
                                  </Tooltip>
                                )
                              )
                            ) : (
                              <Typography variant="body2" color="text.disabled">—</Typography>
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>

                  {jdDetails.preferred_qualifications?.length > 0 && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <PQContainer sx={{ p: 2.5, backgroundColor: "#f8fafc", borderRadius: "12px" }}>
                        <Typography variant="h6" className="pq-heading" sx={{ mb: 1.5, fontWeight: 700, color: "#0f172a" }}>
                          Preferred Qualifications
                        </Typography>
                        <List dense sx={{ pl: 0 }}>
                          {jdDetails.preferred_qualifications.map(
                            (qualification, index) => (
                              <ListItem
                                key={index}
                                sx={{
                                  py: 0.5,
                                  px: 1,
                                  display: "flex",
                                  alignItems: "flex-start",
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 24,
                                    alignSelf: "flex-start",
                                    pt: 0.5,
                                  }}
                                >
                                  <FiberManualRecordIcon
                                    sx={{
                                      fontSize: 8,
                                      color: colors.primary,
                                    }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  className="list-item-text"
                                  primary={qualification}
                                  sx={{
                                    m: 0,
                                    "& .MuiTypography-root": {
                                      color: "#334155",
                                      lineHeight: 1.6,
                                    },
                                  }}
                                />
                              </ListItem>
                            )
                          )}
                        </List>
                      </PQContainer>
                    </>
                  )}

                  {jdDetails.why_join_us && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <WhyJoinContainer>
                        <Typography className="why-join-head" variant="h6" sx={{ mb: 1.5, fontWeight: 700, display: "flex", alignItems: "center", gap: 1, color: "#0f172a" }}>
                          <BusinessIcon color="primary" />
                          Why Join Us?
                        </Typography>
                        <Paper elevation={0} className="why-join-paper" sx={{ p: 2.5, backgroundColor: "#f0fdf4", borderRadius: "12px", border: "1px solid #bbf7d0" }}>
                          <Typography
                            variant="body1"
                            className="why-join-content"
                            sx={{ color: "#166534", lineHeight: 1.7 }}
                          >
                            {jdDetails.why_join_us}
                          </Typography>
                        </Paper>
                      </WhyJoinContainer>
                    </>
                  )}
                </CardContent>
              </StyledCard>
            )}
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default CreateNewJobDescription;
